/**
 * «Вставить перевод»: machine translation of the Russian base text into a translation language,
 * straight from the browser — no Cloud Function, no API key.
 *
 * Uses Google Translate's public web endpoint (`translate.googleapis.com/translate_a/single`,
 * `client=gtx`). Chrome's built-in Translator API was the other option, but it has no Azerbaijani
 * and works in desktop Chrome only. Trade-offs of this endpoint: it is undocumented and unofficial —
 * no SLA, it may rate-limit (HTTP 429) or show a captcha to a busy network, and Google may change or
 * close it. It only ever fills an input the admin then reviews; nothing is saved automatically.
 * The text goes to Google: only the catalog labels an admin is editing (names, questions, hints).
 */

const ENDPOINT = 'https://translate.googleapis.com/translate_a/single'
/** The base language of all catalog content. */
const SOURCE_LANGUAGE = 'ru'
/**
 * Keeps the GET URL well under common limits: measured on the ENCODED text (a Cyrillic letter is
 * 6 bytes once percent-encoded), so roughly 1000 Russian characters.
 */
const MAX_ENCODED_LENGTH = 6000
/** A stalled request gives up instead of spinning forever. */
const TIMEOUT_MS = 15_000

/** Successful results of this session, so repeated clicks do not hit the endpoint again. */
const cache = new Map<string, string>()

export class TranslateError extends Error {}

/** Translates Russian [text] into [target] (an app language tag: 'en', 'az'). */
export async function translateFromRussian(text: string, target: string): Promise<string> {
  const source = text.trim()
  if (source.length === 0) return ''
  if (encodeURIComponent(source).length > MAX_ENCODED_LENGTH) {
    throw new TranslateError('Текст слишком длинный для перевода за раз — переведите его по частям.')
  }
  const cacheKey = `${target}\u0000${source}`
  const cached = cache.get(cacheKey)
  if (cached !== undefined) return cached

  const params = new URLSearchParams({ client: 'gtx', sl: SOURCE_LANGUAGE, tl: target, dt: 't', q: source })
  let response: Response
  try {
    response = await fetch(`${ENDPOINT}?${params.toString()}`, {
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: AbortSignal.timeout(TIMEOUT_MS),
    })
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === 'TimeoutError') {
      throw new TranslateError('Сервис перевода не ответил вовремя — попробуйте ещё раз.')
    }
    throw new TranslateError('Сервис перевода недоступен (нет сети или запрос заблокирован).')
  }
  if (response.status === 429) {
    throw new TranslateError('Google временно ограничил число переводов — попробуйте позже.')
  }
  if (!response.ok) throw new TranslateError(`Сервис перевода ответил ошибкой ${response.status}.`)

  let data: unknown
  try {
    data = await response.json()
  } catch {
    throw new TranslateError('Сервис перевода вернул неожиданный ответ.')
  }
  const result = parseResponse(data)
  if (result === null) throw new TranslateError('Сервис перевода вернул неожиданный ответ.')
  cache.set(cacheKey, result)
  return result
}

/**
 * The endpoint answers `[[["Laminate","Ламинат",…],["…","…",…]], null, "ru", …]`: one entry per
 * sentence, the translation first. Line breaks are kept inside the sentence pieces.
 */
export function parseResponse(data: unknown): string | null {
  if (!Array.isArray(data) || !Array.isArray(data[0])) return null
  const pieces: string[] = []
  for (const sentence of data[0] as unknown[]) {
    if (Array.isArray(sentence) && typeof sentence[0] === 'string') pieces.push(sentence[0])
  }
  const text = pieces.join('').trim()
  return text.length > 0 ? text : null
}

export function describeTranslateError(caught: unknown): string {
  return caught instanceof TranslateError ? caught.message : 'Перевод не удался.'
}
